import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Receipt, DollarSign, Plus, Calendar, MapPin, Star, Filter,
  ChevronDown, Image, CheckCircle, Search, Tag, TrendingUp, 
  TrendingDown, Phone, ExternalLink, ArrowUpRight
} from 'lucide-react';
import API from '@/utils/api';
import { getFileUrl } from '../../../utils/fileStorage';

// Category colors and icons
const CATEGORY_CONFIG = {
  maintenance: { label: 'Maintenance', color: 'bg-blue-100 text-blue-700', darkColor: 'bg-blue-500' },
  repair: { label: 'Repair', color: 'bg-red-100 text-red-700', darkColor: 'bg-red-500' },
  tire: { label: 'Tire', color: 'bg-slate-100 text-slate-700', darkColor: 'bg-slate-500' },
  registration: { label: 'Registration', color: 'bg-green-100 text-green-700', darkColor: 'bg-green-500' },
  insurance: { label: 'Insurance', color: 'bg-purple-100 text-purple-700', darkColor: 'bg-purple-500' },
  fuel: { label: 'Fuel', color: 'bg-amber-100 text-amber-700', darkColor: 'bg-amber-500' },
  parts: { label: 'Parts', color: 'bg-cyan-100 text-cyan-700', darkColor: 'bg-cyan-500' },
  accessory: { label: 'Accessory', color: 'bg-pink-100 text-pink-700', darkColor: 'bg-pink-500' },
  other: { label: 'Other', color: 'bg-slate-100 text-slate-600', darkColor: 'bg-slate-400' },
};

const ReceiptsTabNew = ({ 
  vehicleId,
  healthData,
  onAddReceipt,
  onRefresh,
  theme 
}) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchReceipts = useCallback(async () => {
    try {
      const response = await API.get(`/receipts?vehicle_id=${vehicleId}`);
      setReceipts(response.data);
    } catch (error) {
      console.error('Failed to fetch receipts:', error);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  // Filter receipts
  const filteredReceipts = receipts.filter(r => {
    const matchesFilter = filter === 'all' || r.category === filter;
    const matchesSearch = !searchQuery || 
      r.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.notes?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Calculate category totals
  const categoryTotals = receipts.reduce((acc, r) => {
    const cat = r.category || 'other';
    acc[cat] = (acc[cat] || 0) + (r.amount || 0);
    return acc;
  }, {});

  // Sort categories by total
  const sortedCategories = Object.entries(categoryTotals)
    .sort((a, b) => b[1] - a[1]);

  const totalSpending = sortedCategories.reduce((sum, [_, val]) => sum + val, 0);

  const preferredShops = healthData?.preferred_shops || [];
  const spending = healthData?.spending || {};

  // Calculate spending trend (compare last 30 to previous 30)
  const spendingTrend = spending.last_30_days && spending.last_90_days 
    ? ((spending.last_30_days / ((spending.last_90_days - spending.last_30_days) / 2 || 1)) - 1) * 100
    : 0;

  return (
    <div className="space-y-4">
      {/* Spending Summary - Enhanced */}
      <Card style={{ backgroundColor: theme.surface }} data-testid="receipts-spending-summary">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <DollarSign className="h-5 w-5" style={{ color: theme.icon }} />
            Spending Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Time-based summaries */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[
              { label: '30 Days', value: spending.last_30_days, highlight: true },
              { label: '90 Days', value: spending.last_90_days },
              { label: '1 Year', value: spending.last_year },
              { label: 'Lifetime', value: spending.lifetime },
            ].map(({ label, value, highlight }) => (
              <div 
                key={label} 
                className={`text-center p-3 rounded-xl ${highlight ? 'ring-2 ring-offset-1' : ''}`}
                style={{ 
                  backgroundColor: highlight ? theme.primary : theme.light,
                  ringColor: theme.icon
                }}
              >
                <p className="text-xl font-bold" style={{ color: theme.dark }}>
                  ${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Spending Trend Indicator */}
          {spending.last_30_days > 0 && (
            <div className="flex items-center justify-center gap-2 mb-4 py-2 rounded-lg bg-slate-50">
              {spendingTrend > 10 ? (
                <>
                  <TrendingUp className="h-4 w-4 text-amber-500" />
                  <span className="text-sm text-slate-600">Spending up this month</span>
                </>
              ) : spendingTrend < -10 ? (
                <>
                  <TrendingDown className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-slate-600">Spending down this month</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-600">Spending is consistent</span>
                </>
              )}
            </div>
          )}

          {/* Category breakdown - Visual bars */}
          {sortedCategories.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">By Category</p>
              {sortedCategories.slice(0, 5).map(([cat, total]) => {
                const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.other;
                const percentage = totalSpending > 0 ? (total / totalSpending) * 100 : 0;
                return (
                  <div key={cat} className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 w-20 truncate">{config.label}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${config.darkColor}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-16 text-right">
                      ${total.toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preferred Shops - Enhanced */}
      {preferredShops.length > 0 && (
        <Card style={{ backgroundColor: theme.surface }} data-testid="preferred-shops">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-400 fill-amber-400" />
              Your Preferred Shops
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {preferredShops.slice(0, 5).map((shop, index) => (
                <div 
                  key={shop.name}
                  className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-100 hover:shadow-sm transition-shadow"
                >
                  {/* Rank Badge */}
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                    style={{ 
                      backgroundColor: index === 0 ? '#FEF3C7' : theme.light,
                      color: index === 0 ? '#D97706' : theme.dark
                    }}
                  >
                    {index + 1}
                  </div>
                  
                  {/* Shop Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-slate-800 truncate">{shop.name}</h4>
                      {shop.avg_rating >= 4 && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">
                          <Star className="h-3 w-3 mr-0.5 fill-current" />
                          {shop.avg_rating}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span>{shop.visits} visit{shop.visits !== 1 ? 's' : ''}</span>
                      <span>•</span>
                      <span>${shop.total_spent?.toLocaleString()} spent</span>
                      {shop.last_visit && (
                        <>
                          <span>•</span>
                          <span>Last: {new Date(shop.last_visit).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Action */}
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <ArrowUpRight className="h-4 w-4 text-slate-400" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Receipts List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-800">All Receipts</h3>
          <Button
            size="sm"
            onClick={onAddReceipt}
            style={{ backgroundColor: theme.icon }}
            className="hover:opacity-90"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Receipt
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search receipts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-slate-100' : ''}
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>

        {/* Filter Pills */}
        {showFilters && (
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge
              className={`cursor-pointer ${filter === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}
              onClick={() => setFilter('all')}
            >
              All
            </Badge>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
              <Badge
                key={key}
                className={`cursor-pointer ${filter === key ? 'bg-slate-800 text-white' : config.color}`}
                onClick={() => setFilter(key)}
              >
                {config.label}
              </Badge>
            ))}
          </div>
        )}

        {/* Receipts */}
        {loading ? (
          <div className="text-center py-8 text-slate-400">Loading...</div>
        ) : filteredReceipts.length === 0 ? (
          <Card style={{ backgroundColor: theme.surface }}>
            <CardContent className="py-8 text-center text-slate-400">
              <Receipt className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p className="font-medium text-slate-500">Ready to Track Receipts</p>
              <p className="text-sm">Add your first receipt to build your spending history</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredReceipts.map((receipt) => {
              const catConfig = CATEGORY_CONFIG[receipt.category] || CATEGORY_CONFIG.other;
              
              return (
                <Card 
                  key={receipt.id}
                  className="border hover:shadow-sm transition-shadow cursor-pointer"
                  style={{ backgroundColor: theme.surface }}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      {/* Receipt Image or Icon */}
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: theme.light }}
                      >
                        {receipt.receipt_image ? (
                          <img 
                            src={getFileUrl(receipt.receipt_image)} 
                            alt="Receipt"
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <Receipt className="h-6 w-6" style={{ color: theme.icon }} />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-slate-800">{receipt.vendor_name}</h4>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-sm text-slate-500">{receipt.date}</span>
                              {receipt.mileage && (
                                <span className="text-xs text-slate-400">
                                  • {receipt.mileage.toLocaleString()} mi
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="font-bold text-slate-800">
                            ${receipt.amount?.toLocaleString()}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={`${catConfig.color} text-xs`}>
                            {catConfig.label}
                          </Badge>
                          {receipt.is_reimbursed && (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Reimbursed
                            </Badge>
                          )}
                          {receipt.rating && (
                            <div className="flex items-center gap-1">
                              <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                              <span className="text-xs">{receipt.rating}</span>
                            </div>
                          )}
                        </div>

                        {receipt.notes && (
                          <p className="text-sm text-slate-500 mt-1 line-clamp-1">{receipt.notes}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceiptsTabNew;
